import { FunctionComponent, ReactNode } from "react";
import {
  Box,
  IconButton,
  Link,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  Paper,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";

import { Cell, HeadCell } from "..";
import { formatDateTime } from "../../utils";

type LastEditInfo = {
  createdAt: string | null;
  reason: string | null;
  actorEmail: string | null;
  actorRole: string | null;
  editedFields: string[];
} | null;

type Props = Time & {
  is_ramp_return?: boolean | number;
  original_flight_id?: string | number | null;
  relatedFlights?: {flightId:string;isRampReturn:boolean}[];
  scheduled_time_of_departure: Date;
  commercial_flight_number?: string | null;
  departure_airport?: string | null;
  arrival_airport?: string | null;
  aircraft_registration: string;
  flight_identifier: string;
  timestamp: number;
  insert_timestamp: Date;
  upload_timestamp?: Date;
  lastEdit?: LastEditInfo;
  flightType?: ReactNode;
  showLeonIdRow?: boolean;
  leonId?: string | number | null;
  onLeonRetransfer?: () => void;
  isLeonRetransfering?: boolean;
};

const formatCityPair = (departure?: string | null, arrival?: string | null) =>
  `${departure || "-"} → ${arrival || "-"}`;

export const InfoTable: FunctionComponent<Props> = ({
  is_ramp_return, original_flight_id, relatedFlights,
  scheduled_time_of_departure,
  commercial_flight_number,
  departure_airport,
  arrival_airport,
  aircraft_registration,
  flight_identifier,
  timestamp,
  insert_timestamp,
  upload_timestamp,
  lastEdit,
  flightType,
  showLeonIdRow = false,
  leonId,
  onLeonRetransfer,
  isLeonRetransfering = false,
}) => {
  const commercialNumber = `${commercial_flight_number || ""}`.trim();
  const normalizedLeonId = `${leonId ?? ""}`.trim();
  const hasLeonId = normalizedLeonId.length > 0;
  const retransferDisabled = !hasLeonId || isLeonRetransfering;

  return (
    <TableContainer component={Paper} sx={{ marginBottom: "20px" }}>
      <Table aria-label="info table">
        <TableHead>
          <TableRow>
            <HeadCell>
              <b>Info</b>
            </HeadCell>
            <HeadCell></HeadCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {is_ramp_return ? <TableRow><Cell>Ramp return</Cell><Cell>Original flight <Link href={`/flight/${original_flight_id}`}>#{original_flight_id}</Link></Cell></TableRow> : null}
          {relatedFlights?.length ? <TableRow><Cell>Related attempts</Cell><Cell>{relatedFlights.map(f => <Link key={f.flightId} href={`/flight/${f.flightId}`} sx={{display:"block"}}>{f.isRampReturn ? "Ramp return" : "Original flight"} #{f.flightId}</Link>)}</Cell></TableRow> : null}
          <TableRow>
            <Cell component="th" scope="row">
              STD:
            </Cell>
            <Cell>{formatDateTime(scheduled_time_of_departure)}</Cell>
          </TableRow>

          {commercialNumber ? (
            <TableRow>
              <Cell component="th" scope="row">
                Commercial flight number:
              </Cell>
              <Cell>{commercialNumber}</Cell>
            </TableRow>
          ) : null}

          <TableRow>
            <Cell component="th" scope="row">
              City pair:
            </Cell>
            <Cell>{formatCityPair(departure_airport, arrival_airport)}</Cell>
          </TableRow>

          <TableRow>
            <Cell component="th" scope="row">
              Registration:
            </Cell>
            <Cell>{aircraft_registration}</Cell>
          </TableRow>

          <TableRow>
            <Cell component="th" scope="row">
              Callsign:
            </Cell>
            <Cell>{flight_identifier}</Cell>
          </TableRow>

          {flightType != null ? (
            <TableRow>
              <Cell component="th" scope="row">Flight type:</Cell>
              <Cell>{flightType}</Cell>
            </TableRow>
          ) : null}

          {showLeonIdRow ? (
            <TableRow>
              <Cell component="th" scope="row">
                Leon ID:
              </Cell>
              <Cell>
                <Box
                  sx={{
                    alignItems: "center",
                    display: "flex",
                    gap: 1,
                    justifyContent: "space-between",
                    minWidth: 0,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      alignItems: "center",
                      display: "inline-flex",
                      gap: 0.75,
                      minWidth: 0,
                    }}
                  >
                    {hasLeonId ? (
                      <Box component="span">{normalizedLeonId}</Box>
                    ) : (
                      <Link href="/admin/leon-id" underline="hover">
                        Manual selection
                      </Link>
                    )}
                    <Box
                      component="span"
                      aria-label={hasLeonId ? "Leon ID available" : "Leon ID missing"}
                      sx={{
                        bgcolor: hasLeonId ? "success.main" : "error.main",
                        borderRadius: "50%",
                        display: "inline-block",
                        flex: "0 0 auto",
                        height: 8,
                        width: 8,
                      }}
                    />
                  </Box>
                  {onLeonRetransfer ? (
                    <Tooltip
                      title={
                        hasLeonId
                          ? "Re-transfer Data to Leon"
                          : "Assign a Leon ID before re-transfer"
                      }
                    >
                      <span>
                        <IconButton
                          aria-label="Re-transfer Data to Leon"
                          disabled={retransferDisabled}
                          onClick={onLeonRetransfer}
                          size="small"
                        >
                          <RefreshIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  ) : null}
                </Box>
              </Cell>
            </TableRow>
          ) : null}

          <TableRow>
            <Cell component="th" scope="row">
              Flight finalized by crew:
            </Cell>
            <Cell>{formatDateTime(new Date(timestamp * 1000))}</Cell>
          </TableRow>

          <TableRow>
            <Cell component="th" scope="row">
              Flight uploaded:
            </Cell>
            <Cell>{formatDateTime(insert_timestamp)}</Cell>
          </TableRow>

          {lastEdit ? (
            <TableRow>
              <Cell component="th" scope="row">
                Modified by:
              </Cell>
              <Cell>
                {lastEdit.actorEmail}, Values: {lastEdit.editedFields.join(", ")}
                {lastEdit.createdAt
                  ? ` (${formatDateTime(new Date(lastEdit.createdAt))})`
                  : ""}
              </Cell>
            </TableRow>
          ) : null}

          <TableRow>
            <Cell component="th" scope="row">
              Leon upload time:
            </Cell>
            <Cell>
              {upload_timestamp ? formatDateTime(upload_timestamp) : "—"}
            </Cell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};
